import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { buildTheme } from "./theme";
import type { ThemeMode } from "./brand";

const MODE_KEY = "zekabin.theme.mode";

const detectInitialMode = (): ThemeMode => {
  const stored = localStorage.getItem(MODE_KEY) as ThemeMode | null;
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

interface Ctx {
  mode: ThemeMode;
  toggleMode: () => void;
  setMode: (m: ThemeMode) => void;
}

const ThemeModeCtx = createContext<Ctx | null>(null);

export const AppThemeProvider = ({ children }: { children: ReactNode }) => {
  const [mode, setModeState] = useState<ThemeMode>(detectInitialMode);
  const theme = useMemo(() => buildTheme(mode), [mode]);

  useEffect(() => {
    localStorage.setItem(MODE_KEY, mode);
    document.documentElement.dataset.theme = mode;
  }, [mode]);

  const value: Ctx = {
    mode,
    setMode: setModeState,
    toggleMode: () => setModeState((m) => (m === "light" ? "dark" : "light")),
  };

  return (
    <ThemeModeCtx.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeCtx.Provider>
  );
};

export const useThemeMode = () => {
  const ctx = useContext(ThemeModeCtx);
  if (!ctx) throw new Error("useThemeMode must be used inside AppThemeProvider");
  return ctx;
};
